import { useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import { FileText, ExternalLink } from "lucide-react";

// Phase 4 (2026-02): attachments are PROXIED (never a raw Drive link — most viewers aren't
// Shared Drive members) via GET /v1/community/posts/{id}/attachments/{index}. Fetched as an
// authenticated blob (works for BOTH cookie- and Bearer-token sessions, unlike a bare
// <img src="...">) and rendered via an object URL, revoked on unmount.
// Crop hardening (2026-08, founder-reported): gallery tiles were `aspect-square object-cover`
// — harmless for a landscape source (mild side-crop, easy to miss in review) but a portrait/
// HEIC BTS photo (e.g. 9:16) forced into a 1:1 tile lost the majority of the image off the top
// and bottom, a real crop regardless of viewport width. Switched to `object-contain` (with a
// neutral tile background) so the tile's aspect stays a consistent square in the grid but the
// full photo is always visible, letterboxed rather than cropped — true for every orientation,
// mobile and desktop alike, not just landscape sources.
function SingleAttachment({ postId, index, mimeType, filename, tile, width, height }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [nearby, setNearby] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const root = useRef(null);
  const isImage = (mimeType || "").startsWith("image/");
  const ratio = tile ? "1" : width > 0 && height > 0 ? `${width} / ${height}` : "4 / 3";

  useEffect(() => {
    if (!window.IntersectionObserver) { setNearby(true); return undefined; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { setNearby(true); observer.disconnect(); }
    }, { rootMargin: "300px" });
    observer.observe(root.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!nearby) return undefined;
    const controller = new AbortController();
    let url;
    let active = true;
    setError(false); setObjectUrl(null);
    api.get(`/v1/community/posts/${postId}/attachments/${index}`, {
      responseType: "blob", params: { display: true }, signal: controller.signal, timeout: 60000,
    }).then((r) => {
      if (!active) return;
      url = window.URL.createObjectURL(r.data); setObjectUrl(url);
    }).catch(() => { if (active) setError(true); });
    return () => { active = false; controller.abort(); if (url) window.URL.revokeObjectURL(url); };
  }, [nearby, postId, index, attempt]);

  return (
    <div ref={root} className={`rounded-lg border border-hairline bg-surface2/60 overflow-hidden ${tile ? "w-full" : "w-full max-w-xl"}`}
      style={isImage ? { aspectRatio: ratio } : undefined} aria-busy={nearby && !objectUrl && !error}>
      {error ? <button type="button" className="min-h-[44px] p-3 text-sm" onClick={() => setAttempt((v) => v + 1)}>Retry attachment</button>
        : !objectUrl ? <div className="min-h-[44px]" aria-label="Attachment loading" />
        : isImage ? <img src={objectUrl} alt={filename || "attachment"} data-testid={`community-post-attachment-image-${postId}-${index}`}
          className="w-full h-full object-contain" decoding="async" />
        : <a href={objectUrl} target="_blank" rel="noreferrer" data-testid={`community-post-attachment-file-${postId}-${index}`}
            className="flex min-h-[44px] items-center gap-2 p-3 text-xs text-ink-soft">
            <FileText className="h-4 w-4 shrink-0" /> {filename || "Attachment"} <ExternalLink className="h-3 w-3 ml-auto" />
          </a>}
    </div>
  );
}

// Item 4 rework (2026-02): a post's `attachments` array is 1..6 real images, or exactly 1 PDF —
// never mixed (see modules/community.py::create_post). One attachment renders full-width;
// several render as a compact grid gallery.
export function PostAttachments({ postId, attachments }) {
  if (!attachments || attachments.length === 0) return null;
  const isGallery = attachments.length > 1;
  // Grid hardening (2026-08, iteration_187 finding): a fixed grid-cols-3 left a visibly empty
  // third column for a 2-image gallery — 2 attachments now get a 2-column grid, 3+ keep 3.
  const gridCols = attachments.length === 2 ? "grid-cols-2" : "grid-cols-3";

  return (
    <div className={`mt-2.5 ${isGallery ? `grid ${gridCols} gap-1.5` : ""}`} data-testid={`community-post-attachments-${postId}`}>
      {attachments.map((a, i) => (
        <SingleAttachment key={i} postId={postId} index={i} mimeType={a.mime_type} filename={a.filename} tile={isGallery} width={a.width} height={a.height} />
      ))}
    </div>
  );
}
